import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { type NavigateOptions } from '@tanstack/react-router';

import usersApiSlice from '@/api/endpoints/users';
import { getFilterNavigateOptions } from '@/features/navigation/filterNavigationConfig';
import { setGlobalFilterChangePending } from '@/features/users';

interface FilterChangeMessage {
  type: 'windowReload';
}

/**
 * Tracks whether the current `getUserInfo` refetch was triggered by one of the
 * four global-filter mutations. We only want to navigate (and ungate queries)
 * when a mutation is the cause — not for unrelated refetches such as
 * window-focus re-fetches.
 */
let pendingMutationActive = false;

/**
 * BroadcastChannel used to notify other open tabs that the global
 * filter has changed so they can reload their data.
 */
const filterChangeChannel = new BroadcastChannel('filterChange');
filterChangeChannel.onmessage = (_event: MessageEvent<FilterChangeMessage>) => {
  window.location.reload();
};

const filterChangePostMessage = (message: FilterChangeMessage) => {
  filterChangeChannel.postMessage(message);
};

export const globalFilterListenerMiddleware = createListenerMiddleware();

const { startListening } = globalFilterListenerMiddleware;

/**
 * Listener A — fired when any filter-selection mutation starts.
 *
 * Sets `filterChangePending: true` in Redux so that `useGlobalFilterKeys`
 * returns `isGlobalFilterReady: false`, causing all dependent queries to skip
 * immediately — before React has a chance to re-render with new filter keys.
 */
startListening({
  matcher: isAnyOf(
    usersApiSlice.endpoints.selectPartner.matchPending,
    usersApiSlice.endpoints.selectPlatform.matchPending,
    usersApiSlice.endpoints.selectAdvertiser.matchPending,
    usersApiSlice.endpoints.selectAccount.matchPending
  ),
  effect: (_, listenerApi) => {
    pendingMutationActive = true;
    listenerApi.dispatch(setGlobalFilterChangePending(true));
  },
});

/**
 * Listener B — fired when `getUserInfo` is fulfilled.
 *
 * If the fulfillment was preceded by a filter mutation (tracked via
 * `pendingMutationActive`), this listener:
 *   1. Navigates to the current route with the correct preserved/reset search
 *      params so that all route-schema defaults are applied.
 *   2. Posts a BroadcastChannel message so other open tabs reload.
 *   3. Sets `filterChangePending: false` to ungate dependent queries.
 *
 * Because step 3 happens only after navigation resolves, all downstream queries
 * (e.g. `getMetrics`) fire exactly once with the final URL search params.
 */
startListening({
  matcher: usersApiSlice.endpoints.getUserInfo.matchFulfilled,
  effect: async (_, listenerApi) => {
    if (!pendingMutationActive) {
      return;
    }
    pendingMutationActive = false;

    // Lazy import to break the potential circular dependency:
    // store → listenerMiddleware → router → store
    const { router } = await import('../router');

    const { pathname } = router.state.location;
    const navOptions = getFilterNavigateOptions(pathname);
    // `FilterNavigateOptions.search` uses `Record<string, unknown>` because
    // keys are picked at runtime via `Object.fromEntries`. This is structurally
    // incompatible with `NavigateOptions.search`, which is typed to the full
    // route-schema union via deep generics. The cast is safe: `to` is always a
    // valid registered path, and the preserved keys are validated at the
    // `filterConfig` definition site.
    await router.navigate(navOptions as NavigateOptions);

    filterChangePostMessage({ type: 'windowReload' });

    listenerApi.dispatch(setGlobalFilterChangePending(false));
  },
});
